import React from 'react';

import { useIntl } from '../../util/reactIntl';
import { composeValidators, required } from '../../util/validators';

import FieldTextInput from '../FieldTextInput/FieldTextInput';

import css from './AddressFields.module.css';

// Norwegian postal codes are always 4 digits.
const POSTCODE_REGEX = /^\d{4}$/;
const postcodeFormat = message => value =>
  value && !POSTCODE_REGEX.test(String(value).trim()) ? message : undefined;

/**
 * Shared address subform — drops into any parent React Final Form. Used
 * on ContactDetailsPage (default sender/delivery addresses), the Frakt
 * tab (per-listing sender override), and CheckoutPage (per-purchase
 * delivery override). The Bring booking uses these fields directly when
 * constructing the parcel label (see `server/api/shipping/shipment-builder.js`).
 *
 * Field naming: the parent prefixes every field with a namespace so we
 * can host multiple address forms on the same page without collisions.
 * Example: `<AddressFields namespace="shippingAddress" />` produces
 * `shippingAddress.name`, `shippingAddress.addressLine`, etc.
 *
 * Phone + email aren't captured here — the Bring contact block reuses
 * the user's primary email/phone from `currentUser`, so we don't ask
 * for the same info twice.
 *
 * @component
 * @param {Object} props
 * @param {string} props.namespace   Field-name prefix (e.g. "shippingAddress").
 * @param {string} props.formId      Stable form id, used to build input ids.
 * @param {boolean} [props.disabled] Whether to disable every input.
 */
const AddressFields = ({ namespace, formId, disabled = false }) => {
  const intl = useIntl();
  const ns = namespace ? `${namespace}.` : '';

  const requiredMsg = key => intl.formatMessage({ id: `AddressFields.${key}Required` });
  const formatMsg = key => intl.formatMessage({ id: `AddressFields.${key}Invalid` });

  return (
    <div className={css.root}>
      <FieldTextInput
        id={`${formId}.${ns}name`}
        name={`${ns}name`}
        type="text"
        label={intl.formatMessage({ id: 'AddressFields.nameLabel' })}
        placeholder={intl.formatMessage({ id: 'AddressFields.namePlaceholder' })}
        validate={required(requiredMsg('name'))}
        disabled={disabled}
      />

      <FieldTextInput
        id={`${formId}.${ns}addressLine`}
        name={`${ns}addressLine`}
        type="text"
        label={intl.formatMessage({ id: 'AddressFields.addressLineLabel' })}
        placeholder={intl.formatMessage({ id: 'AddressFields.addressLinePlaceholder' })}
        validate={required(requiredMsg('addressLine'))}
        disabled={disabled}
      />

      <FieldTextInput
        id={`${formId}.${ns}addressLine2`}
        name={`${ns}addressLine2`}
        type="text"
        label={intl.formatMessage({ id: 'AddressFields.addressLine2Label' })}
        placeholder={intl.formatMessage({ id: 'AddressFields.addressLine2Placeholder' })}
        disabled={disabled}
      />

      <div className={css.row}>
        <FieldTextInput
          id={`${formId}.${ns}postalCode`}
          name={`${ns}postalCode`}
          className={css.postalCode}
          type="text"
          label={intl.formatMessage({ id: 'AddressFields.postalCodeLabel' })}
          placeholder="0185"
          validate={composeValidators(
            required(requiredMsg('postalCode')),
            postcodeFormat(formatMsg('postalCode'))
          )}
          disabled={disabled}
          maxLength={4}
          inputMode="numeric"
          autoComplete="postal-code"
        />

        <FieldTextInput
          id={`${formId}.${ns}city`}
          name={`${ns}city`}
          className={css.city}
          type="text"
          label={intl.formatMessage({ id: 'AddressFields.cityLabel' })}
          placeholder={intl.formatMessage({ id: 'AddressFields.cityPlaceholder' })}
          validate={required(requiredMsg('city'))}
          disabled={disabled}
          autoComplete="address-level2"
        />
      </div>

    </div>
  );
};

export default AddressFields;
